import type { GridItemChangeEvent } from "@tql/ui-components-react/kendo/grid";
import { useCallback, useState } from "react";

import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import type { RoutingGuideType } from "~/shared/schemas/routing-guide-schema.ts";

import { useCreateRoutingGuide } from "../../../hooks/use-create-routing-guide.ts";
import { useGetRoutingGuideEnums } from "../../../hooks/use-get-routing-guide-enums.ts";
import { useUpdateRoutingGuide } from "../../../hooks/use-update-routing-guide.ts";
import { initialCarrierRule, initialRule } from "../utils/constants.ts";
import type {
	AutoSelectOption,
	CarrierRule,
	RowUpdate,
	Rule,
} from "../utils/types.ts";
import { applyRowChange, removeRow } from "../utils/update-utils.ts";
import { useCarrierColumns } from "./use-carrier-columns.tsx";
import { useRuleColumns } from "./use-rule-columns.tsx";

export const useRoutingGuideModal = ({
	onClose,
	routingGuide,
}: {
	onClose: () => void;
	routingGuide: RoutingGuideType | null;
}) => {
	const [name, setName] = useState(routingGuide?.name ?? "");

	const [rules, setRules] = useState<Rule[]>(
		routingGuide ? (routingGuide.rules as Rule[]) : [initialRule],
	);

	const [carriers, setCarriers] = useState<CarrierRule[]>(
		routingGuide
			? (routingGuide.carrier as CarrierRule[])
			: [initialCarrierRule],
	);

	const [selectedAutoSelectOption, setSelectedAutoSelectOption] =
		useState<AutoSelectOption>(
			routingGuide?.selectionMethod === 2
				? "lowestCostAndTransitTime"
				: "lowestCost",
		);

	const [isAutoSelectActive, setIsAutoSelectActive] = useState(
		routingGuide?.isAutoSelected ?? false,
	);

	const [showActivationErrors, setShowActivationErrors] = useState(false);
	const [showNameError, setShowNameError] = useState(false);

	const { createRoutingGuide } = useCreateRoutingGuide();
	const { updateRoutingGuide } = useUpdateRoutingGuide();
	const { routingGuideEnums } = useGetRoutingGuideEnums();
	const { showError } = useNotificationActions();

	const handleClose = useCallback(() => {
		setName("");
		setRules([initialRule]);
		setCarriers([initialCarrierRule]);
		onClose();
	}, [onClose]);

	const handleSave = useCallback(
		(isActive: boolean) => {
			if (!name.trim()) {
				setShowNameError(true);
				return;
			}
			setShowNameError(false);
			if (rules.length === 0) {
				showError("At least one rule is required.");
				return;
			}
			if (isActive) {
				const ruleFields = [
					"field1",
					"field2",
					"field3",
					"field4",
					"field5",
					"field6",
				] as const;
				const hasFilledRule = rules.some((r) =>
					ruleFields.some((f) => (r[f] as { value: string })?.value),
				);
				const hasFilledCarrier = carriers.some((c) => c.field1?.value);
				if (!hasFilledRule || !hasFilledCarrier) {
					setShowActivationErrors(true);
					return;
				}
			}
			setShowActivationErrors(false);

			const payload = {
				name,
				rules: rules as RoutingGuideType["rules"],
				carrier: carriers as RoutingGuideType["carrier"],
				isAutoSelected: isAutoSelectActive,
				selectionMethod: selectedAutoSelectOption === "lowestCost" ? 1 : 2,
				isActive,
				isGlobal: routingGuide?.customerId == null,
				customerId: routingGuide?.customerId ?? null,
			};

			if (!routingGuide || routingGuide.isNewGuide) {
				createRoutingGuide(payload);
			} else if (routingGuide.id) {
				updateRoutingGuide({ ...payload, id: routingGuide.id });
			}
			handleClose();
		},
		[
			name,
			routingGuide,
			rules,
			carriers,
			isAutoSelectActive,
			selectedAutoSelectOption,
			handleClose,
			createRoutingGuide,
			updateRoutingGuide,
			showError,
		],
	);

	const addRule = useCallback(() => {
		setRules((prev) => [
			...prev,
			{
				...initialRule,
				id: prev.length > 0 ? prev.length + 1 : 0,
			},
		]);
	}, []);

	const addCarrier = useCallback(() => {
		setCarriers((prev) => [
			...prev,
			{
				...initialCarrierRule,
				id: prev.length > 0 ? prev.length + 1 : 0,
			},
		]);
	}, []);

	const onRuleChange = useCallback(
		(e: GridItemChangeEvent | RowUpdate<Rule>) => {
			setShowActivationErrors(false);
			applyRowChange(e, setRules, routingGuideEnums);
		},
		[setRules, routingGuideEnums],
	);
	const onRuleRemove = useCallback(
		(id: number) => removeRow(id, setRules),
		[setRules],
	);
	const onCarrierChange = useCallback(
		(e: GridItemChangeEvent | RowUpdate<CarrierRule>): void => {
			setShowActivationErrors(false);
			applyRowChange(e, setCarriers, routingGuideEnums);
		},
		[setCarriers, routingGuideEnums],
	);
	const onCarrierRemove = useCallback(
		(id: number) => removeRow(id, setCarriers),
		[setCarriers],
	);

	const ruleColumns = useRuleColumns(
		onRuleChange,
		onRuleRemove,
		showActivationErrors,
		routingGuideEnums,
	);
	const carrierColumns = useCarrierColumns(
		onCarrierChange,
		onCarrierRemove,
		showActivationErrors,
		routingGuideEnums,
	);

	const clearNameError = useCallback(() => {
		setShowNameError(false);
	}, []);

	return {
		name,
		setName,
		clearNameError,
		rules,
		carriers,
		isAutoSelectActive,
		setIsAutoSelectActive,
		selectedAutoSelectOption,
		setSelectedAutoSelectOption,
		ruleColumns,
		carrierColumns,
		handleClose,
		handleSave,
		addRule,
		addCarrier,
		onRuleChange,
		onCarrierChange,
		showNameError,
	};
};
